import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Container from '@mui/material/Container';
import { useForm, useTranslation } from 'hooks';
import { Loader, Section } from 'components/styledComponents';
import { Address, Contact, Language, Personal, Photo, Social } from './forms';
import { parseData, serializeData } from './dataHelpers';
import { Button, FooterActions, FooterDelProfile, ButtonDelProfile } from './style';
import { Trash } from 'components/icons';
import DeleteProfile from './DeleteProfile/DeleteProfile';
import { useNavigate } from 'react-router-dom';

function MyAccountPage({
  language,
  loading,
  personalDetails,
  syncErrors,
  syncing,
  onSave,
  spinnerSize = '40'
}) {
  const { t } = useTranslation();
  const [syncInitiated, setSyncInitiated] = useState(false);
  const navigate = useNavigate();

  const handleClose = useCallback(() => navigate('/'), [navigate]);
  const handleSave = useCallback(
    data => {
      onSave(serializeData(data));
      setSyncInitiated(true);
    },
    [onSave]
  );

  const { errors, useInput, handleAdd, handleRemove, handleSubmit, handleResetError } = useForm({
    initialValues: useMemo(() => ({ ...parseData(personalDetails), language }), [
      personalDetails,
      language
    ]),
    validationSchema: 'accountDetailsSchema',
    onSubmit: handleSave
  });

  useEffect(() => {
    if (syncInitiated && !syncing) {
      if (!syncErrors) handleClose();
      setSyncInitiated(false);
    }
  }, [syncInitiated, syncing, syncErrors, handleClose]);

  // Mobile view state
  const [isMobile, setIsMobile] = useState(window.matchMedia('(max-width: 522px)').matches);

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 522px)');
    const handleResize = e => setIsMobile(e.matches);
    mql.addEventListener('change', handleResize);
    return () => mql.removeEventListener('change', handleResize);
  }, []);

  const renderDeleteButton = () => (
    <FooterDelProfile>
      <ButtonDelProfile
        color="danger"
        contentFirst
        label={t('button.deleteMyProfile')}
        variant="text"
        onClick={() => navigate('/account/deleteProfile')}
      >
        <Trash />
      </ButtonDelProfile>
    </FooterDelProfile>
  );

  return (
    <>
      <Section>
        <Loader component={Container} loading={loading || syncing} spinnerSize={spinnerSize}>
          <Photo useInput={useInput} />
          <Personal useInput={useInput} />
          <DeleteProfile />
        </Loader>
      </Section>

      <Section>
        <Loader component={Container} loading={loading || syncing} spinnerSize={spinnerSize}>
          <Address useInput={useInput} />
          <Contact
            useInput={useInput}
            formErrors={errors}
            onAdd={handleAdd}
            onDelete={handleRemove}
            onResetError={handleResetError}
          />
        </Loader>
      </Section>

      <Section>
        <Loader component={Container} loading={loading || syncing} spinnerSize={spinnerSize}>
          <Social
            useInput={useInput}
            onAdd={handleAdd}
            onDelete={handleRemove}
            onResetError={handleResetError}
          />
          <Language useInput={useInput} />
          <FooterActions>
            {!isMobile && renderDeleteButton()}
            <Button label={t('button.cancel')} variant="text" onClick={handleClose} />
            <Button
              id="profile-submit"
              color="primary"
              disabled={loading || syncing}
              label={t('button.save')}
              onClick={handleSubmit}
            />
          </FooterActions>
          {isMobile && renderDeleteButton()}
        </Loader>
      </Section>
    </>
  );
}

MyAccountPage.propTypes = {
  language: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  personalDetails: PropTypes.object,
  syncErrors: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  syncing: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  spinnerSize: PropTypes.string
};

export default MyAccountPage;
